import './styles.scss'
import React, { useState, useRef, useEffect } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { Galleria } from 'primereact/galleria'
import { FileUpload } from 'primereact/fileupload'
import { Toast } from 'primereact/toast'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { getLocalUserDetails } from 'Utils/LocalStorageHandler'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Loader from 'Components/Loader'
import { IMAGE_UPLOAD_PARAMETERS } from 'Utils/Constants'

const EditModalComponent = ({ setDeletedNewsIdMedia, renderCustomEdit, setRenderCustomEdit, isModalOpen, closeModal, defaultImages, setNewsFeedMedia, newsFeedMedia, setLoading, setIsButtonDisabled, isInputDisable, mockEditNewsFeedModal, mockUploadedFilesMobile, mockUploadedFilesWeb, mockEditNewsFeedModalApiFlag, mockDeviceType }) => {
  const galleria = useRef(null)
  const toast = useRef()
  const hasUpdated = useRef(false)
  const currentTimeInUserTimeZone = new Date()
  const { t } = useTranslation()
  const currentTime = moment(currentTimeInUserTimeZone).format('YYYY-MM-DDTHH:mm:ss')
  const userDetails = getLocalUserDetails()
  const { webImageSizeLimit, mobileImageSizeLimit, mobileImageResolutionWidth, mobileImageResolutionHeight, oneKB, videoMaxDurationMins } = IMAGE_UPLOAD_PARAMETERS

  const [submitButtonDisable, setSubmitButtonDisable] = useState(true)
  const [selectedFileForWeb, setSelectedFileForWeb] = useState([])
  const [selectedFileForMobile, setSelectedFileForMobile] = useState([])
  const [activeIndex, setActiveIndex] = useState(defaultImages?.length > 0 ? 0 : -1)
  const [uploadedFilesWeb, setUploadedFilesWeb] = useState([])
  const [uploadedFilesMobile, setUploadedFilesMobile] = useState([])
  const [modalState, setModalState] = useState({
    showModal: false,
    message: '',
    modalType: '',
    softAction: '',
    onRetry: null
  })
  const [deletedNewsId, setDeletedNewsId] = useState([])
  const [preSelectedImage, setPreSelectedImage] = useState([])
  const [selectedDefaultImages, setSelectedDefaultImages] = useState([])
  const [submittedWebImages, setSubmittedWebImages] = useState([])
  const [submittedMobileImages, setSubmittedMobileImages] = useState([])

  const thumbnailTemplate = (image) => <img src={image.imageUrl} alt={image.alt} className="thumbnail-image" />

  useEffect(() => {
    setPreSelectedImage(newsFeedMedia.filter((item) => item?.defaultImageId === 0))
    if (newsFeedMedia?.length > 0 && !hasUpdated.current) {
      setSelectedDefaultImages(newsFeedMedia.filter((item) => item?.defaultImageId !== 0))
      hasUpdated.current = true
    }
  }, [newsFeedMedia?.length])

  // useEffect to initialize state for testing purposes
  useEffect(() => {
    if (mockEditNewsFeedModal) {
      setUploadedFilesMobile(mockUploadedFilesMobile)
      setUploadedFilesWeb(mockUploadedFilesWeb)
    } else if (mockEditNewsFeedModalApiFlag) {
      handleMediaUpload(mockEditNewsFeedModalApiFlag, mockDeviceType)
    }
  }, [])

  const onDismissFunction = () => {
    setModalState({ showModal: false })
  }

  const handleFileSelect = (event, device) => {
    event?.files?.forEach((ele) => {
      const allowedFormats = ['jpg', 'jpeg', 'png', 'webp', 'mp4']

      const selectedFileExtension = ele?.name?.split('.')?.pop()

      if (!allowedFormats.includes(selectedFileExtension)) {
        toast.current.show({ severity: t('ERROR_TOAST'), summary: t('INVALID_MEDIA_FORMAT'), detail: t('PLEASE_SELECT_VALID_MEDIA_FORMATS') })

        return
      }

      const selectedMedia = event.files && event.files[0]

      if (device === 'web') {
        if (selectedMedia && !uploadedFilesWeb.some((file) => file.name === selectedMedia.name)) {
          checkForFileSize('web', [...selectedFileForWeb, selectedMedia], webImageSizeLimit, 'mobile', selectedFileForMobile, mobileImageSizeLimit)
          setUploadedFilesWeb((prevUploadedFiles) => [...prevUploadedFiles, selectedMedia])
          setSelectedFileForWeb((prevSelectedFile) => [...prevSelectedFile, selectedMedia])
        }
      } else if (device === 'mobile') {
        if (selectedMedia.type.startsWith('image/')) {
          const img = new Image()

          img.src = URL.createObjectURL(selectedMedia)

          img.onload = () => {
            const width = img.width
            const height = img.height

            const isResolutionExceeded = !(width === mobileImageResolutionWidth && height === mobileImageResolutionHeight)

            processMediaUpload(selectedMedia, isResolutionExceeded, null)
          }
        } else if (selectedMedia.type.startsWith('video/')) {
          const video = document.createElement('video')

          video.src = URL.createObjectURL(selectedMedia)

          video.onloadedmetadata = () => {
            const duration = video.duration

            const isDurationExceeded = duration > videoMaxDurationMins * 60

            processMediaUpload(selectedMedia, false, isDurationExceeded)
          }
        }

        const processMediaUpload = (media, resolutionExceeded, durationExceeded) => {
          if (media && !uploadedFilesMobile.some((file) => file.selectedMedia.name === media.name)) {
            const updatedMobileObject = {
              selectedMedia: media,
              isResolutionExceeded: resolutionExceeded,
              isDurationExceeded: durationExceeded
            }

            checkForFileSize('mobile', [...selectedFileForMobile, updatedMobileObject], mobileImageSizeLimit, 'web', selectedFileForWeb, webImageSizeLimit)
            setUploadedFilesMobile((prevUploadedFiles) => [...prevUploadedFiles, updatedMobileObject])
            setSelectedFileForMobile((prevSelectedFile) => [...prevSelectedFile, updatedMobileObject])
          }
        }
      }
    })
  }

  const handleImageSubmit = async () => {
    closeModal()
    setLoading(true)

    const s3Images = []

    const filteredSelectedDefaultImage = selectedDefaultImages.filter((el) => !el.idNewsFeedMedia && el?.defaultImageId)
    const filteredUploadedDefaultImages = selectedDefaultImages.filter((el) => el.idNewsFeedMedia && el.defaultImageId !== 0)

    const selectedImageObjects = filteredSelectedDefaultImage.flatMap((el) => {
      const { imageUrl, mobileImageUrl, nameOfMedia, typeOfMedia, uploadedFileName, extensionOfMedia, sizeOfMedia, idImage, typeMedia, webimgpath, mobimgpath, defaultImageId } = el

      const currentTimeString = new Date(currentTime).toJSON().toString()

      const baseObject = {
        idNewsFeedMedia: 0,
        idNewsFeed: 0,
        createdOn: currentTimeString,
        createdBy: userDetails.id,
        updateOn: currentTimeString,
        updatedBy: 0,
        typeMedia: typeOfMedia || typeMedia,
        isActive: true,
        nameOfMedia: nameOfMedia,
        extensionOfMedia: extensionOfMedia,
        pathOfMedia: '',
        sizeOfMedia: sizeOfMedia,
        mediaData: nameOfMedia,
        uploadedFileName: uploadedFileName,
        defaultImages: true,
        defaultImageId: idImage || defaultImageId
      }

      const objectForDesktop = { ...baseObject, imgflag: 1, webimgpath: imageUrl || webimgpath, mobimgpath: '' }

      const objectForMobile = { ...baseObject, imgflag: 2, webimgpath: '', mobimgpath: mobileImageUrl || mobimgpath }

      return [objectForDesktop, objectForMobile]
    })

    const promises = []

    const createMediaObject = (file, url, isMobileMedia, index) => {
      const { name, size, type } = file

      if (!newsFeedMedia?.length || newsFeedMedia.every((file) => file.nameOfMedia !== name)) {
        const currentTimeString = new Date(currentTime).toJSON().toString()

        const baseObject = {
          idNewsFeedMedia: 0,
          idNewsFeed: 0,
          createdOn: currentTimeString,
          createdBy: userDetails.id,
          updatedOn: currentTimeString,
          updatedBy: 0,
          typeMedia: type.split('/')[0],
          isActive: true,
          extensionOfMedia: type.split('/')[1],
          sizeOfMedia: size,
          nameOfMedia: name,
          mediaData: type.split('/')[0],
          uploadedFileName: url.split('/')[3],
          defaultImages: false,
          selectedIndex: index,
          pathOfMedia: ''
        }

        return isMobileMedia ? { ...baseObject, webimgpath: '', mobimgpath: url, imgflag: 2 } : { ...baseObject, webimgpath: url, mobimgpath: '', imgflag: 1 }
      }
    }

    if (selectedFileForWeb?.length > 0) {
      setSubmittedWebImages([...selectedFileForWeb])
      selectedFileForWeb.forEach((file, index) => {
        promises.push(
          (async () => {
            const webUrl = await handleMediaUpload(file, 'web')
            const mediaObject = createMediaObject(file, webUrl, false, index)

            s3Images.push(mediaObject)
          })()
        )
      })
    }

    if (selectedFileForMobile?.length > 0) {
      setSubmittedMobileImages([...selectedFileForMobile])
      selectedFileForMobile?.forEach((file, index) => {
        promises.push(
          (async () => {
            const mobileUrl = await handleMediaUpload(file.selectedMedia, 'mobile')
            const mediaObject = createMediaObject(file.selectedMedia, mobileUrl, true, index)

            s3Images.push(mediaObject)
          })()
        )
      })
    }

    await Promise.all(promises)

    const allImages = [...filteredUploadedDefaultImages, ...preSelectedImage, ...selectedImageObjects.filter((el) => el.webimgpath || el?.mobimgpath), ...s3Images]

    setSelectedDefaultImages([...filteredUploadedDefaultImages, ...selectedImageObjects.filter((el) => el.webimgpath || el?.mobimgpath)])
    setLoading(false)

    const filteredNewsFeed = newsFeedMedia.filter((item) => item.defaultImageId !== 0)

    const preSelectedIds = selectedDefaultImages.map((item) => item.idNewsFeedMedia)

    let missingIds = filteredNewsFeed.filter((item) => !preSelectedIds.includes(item.idNewsFeedMedia)).map((item) => item.idNewsFeedMedia)

    if (deletedNewsId?.length > 0 || missingIds?.length > 0) {
      const allIds = [...deletedNewsId, ...missingIds]

      setDeletedNewsIdMedia((prev) => [...prev, ...allIds.map((id) => [id])])

      missingIds = []
    }

    setDeletedNewsId([])
    setNewsFeedMedia(allImages)
    setIsButtonDisabled(false)
  }

  const handleMediaUpload = async (file, deviceType) => {
    const fileData = new FormData()

    fileData.append('file', file)

    const uploadMethod = deviceType === 'web' ? apiAdapterCoffeeWeb.uploadFileToS3Bucket : apiAdapterCoffeeWeb.uploadFileToS3BucketMobile

    const { data } = await uploadMethod(fileData)

    return data
  }

  const handleCancel = (item, index) => {
    if (item.defaultImageId != null) {
      preSelectedImage.splice(index, 1)
      setDeletedNewsId((prev) => [...prev, item.idNewsFeedMedia])

      setPreSelectedImage([...preSelectedImage])
    }

    if (selectedFileForWeb?.length > 0 && selectedFileForMobile?.length > 0) {
      checkForFileSize('web', selectedFileForWeb, webImageSizeLimit, 'mobile', selectedFileForMobile, mobileImageSizeLimit)
    } else if (selectedFileForWeb?.length > 0 && !selectedFileForMobile?.length) {
      isFileSizeExceeded('web', selectedFileForWeb, webImageSizeLimit)
    } else if (selectedFileForMobile?.length > 0 && !selectedFileForWeb?.length > 0) {
      isFileSizeExceeded('mobile', selectedFileForMobile, mobileImageSizeLimit)
    } else if (!selectedFileForMobile?.length > 0 && !selectedFileForWeb?.length > 0) {
      setSubmitButtonDisable(false)
    }
  }

  const removeImage = (index, nameOfMedia, idNewsFeedMedia, defaultImageUrl, device) => {
    setIsButtonDisabled(false)
    const updatedNewsFeedMedia = [...newsFeedMedia]
    const removedImage = updatedNewsFeedMedia[index]

    if (removedImage) {
      if (idNewsFeedMedia) {
        setDeletedNewsIdMedia((prev) => [...prev, idNewsFeedMedia])
      }
      updatedNewsFeedMedia.splice(index, 1)
      setNewsFeedMedia(updatedNewsFeedMedia)

      if ((removedImage?.idNewsFeedMedia && removedImage?.defaultImageId) || removedImage?.defaultImages) {
        const defaultImagesRemaining = selectedDefaultImages.filter((el) => (device === 'web' ? el?.webimgpath !== defaultImageUrl : el?.mobimgpath !== defaultImageUrl))

        setSelectedDefaultImages([...defaultImagesRemaining])
      } else {
        const updatedUploadedFilesWeb = uploadedFilesWeb.filter((file) => file.name !== nameOfMedia)
        const updatedUploadedFilesMobile = uploadedFilesMobile.filter((file) => file.selectedMedia.name !== nameOfMedia)
        const updatedSelectedFilesForWeb = selectedFileForWeb.filter((file) => file.name !== nameOfMedia)
        const updatedSelectedFilesForMobile = selectedFileForMobile.filter((file) => file.selectedMedia.name !== nameOfMedia)

        setUploadedFilesWeb(updatedUploadedFilesWeb)
        setUploadedFilesMobile(updatedUploadedFilesMobile)
        setSelectedFileForWeb(updatedSelectedFilesForWeb)
        setSelectedFileForMobile(updatedSelectedFilesForMobile)
        setSubmittedWebImages(updatedUploadedFilesWeb)
        setSubmittedMobileImages(updatedUploadedFilesMobile)
      }
    }
  }

  const handleImageClick = (index, idImage, nameOfMedia) => {
    let updatedSelectedImages

    const isSelected = selectedDefaultImages.some((el) => el?.defaultImageId === idImage || el?.idImage === idImage)

    if (!isSelected) {
      updatedSelectedImages = [...selectedDefaultImages, { ...defaultImages[index], defaultImageId: idImage }]
    } else {
      updatedSelectedImages = selectedDefaultImages.filter((el) => el?.nameOfMedia !== nameOfMedia)
    }

    setSelectedDefaultImages([...updatedSelectedImages])

    if (selectedFileForWeb?.length > 0 && selectedFileForMobile?.length > 0) {
      checkForFileSize('web', selectedFileForWeb, webImageSizeLimit, 'mobile', selectedFileForMobile, mobileImageSizeLimit)
    } else if (selectedFileForWeb?.length > 0 && !selectedFileForMobile?.length) {
      isFileSizeExceeded('web', selectedFileForWeb, webImageSizeLimit)
    } else if (selectedFileForMobile?.length > 0 && !selectedFileForWeb?.length > 0) {
      isFileSizeExceeded('mobile', selectedFileForMobile, mobileImageSizeLimit)
    } else if (!selectedFileForMobile?.length > 0 && !selectedFileForWeb?.length > 0) {
      setSubmitButtonDisable(false)
    }
  }

  const handleCancelClick = (index, device) => {
    if (device === 'web') {
      const remainingUploadedImages = selectedFileForWeb.filter((_, ind) => ind !== index)

      setSelectedFileForWeb([...remainingUploadedImages])
      setUploadedFilesWeb([...remainingUploadedImages])
      checkForFileSize('web', remainingUploadedImages, webImageSizeLimit, 'mobile', selectedFileForMobile, mobileImageSizeLimit)
    } else if (device === 'mobile') {
      const remainingUploadedImages = selectedFileForMobile.filter((_, ind) => ind !== index)

      setSelectedFileForMobile([...remainingUploadedImages])
      setUploadedFilesMobile([...remainingUploadedImages])
      checkForFileSize('mobile', remainingUploadedImages, mobileImageSizeLimit, 'web', selectedFileForWeb, webImageSizeLimit)
    }
  }

  const deleteNewsMediaAsset = async (newsFeedMediaId) => {
    setLoading(true)
    try {
      await apiAdapterCoffeeWeb.deleteNewsMedia(newsFeedMediaId)
    } catch {
      setModalState({
        showModal: true,
        message: t('FAILED_TO_DELETE_IMAGE'),
        modalType: 'error',
        onRetry: deleteNewsMediaAsset
      })
    } finally {
      setLoading(false)
    }
  }

  const checkForFileSize = (primaryDevice, primaryFile, primaryFileSize, secondaryDevice, secondaryFile, secondaryFileSize) => {
    isFileSizeExceeded(primaryDevice, [...primaryFile], primaryFileSize)
    if (secondaryFile?.length > 0) {
      isFileSizeExceeded(secondaryDevice, [...secondaryFile], secondaryFileSize)
    }
  }

  let isWebSizeExceeded = false
  let isMobileSizeExceeded = false

  const isFileSizeExceeded = (deviceType, uploadedFile, fileSize) => {
    if (deviceType === 'web') {
      isWebSizeExceeded = uploadedFile.some((file) => file.type.startsWith('image/') && Math.floor(file.size / (oneKB * oneKB)) > fileSize)
    }

    if (deviceType === 'mobile') {
      isMobileSizeExceeded = uploadedFile.some((file) => file.selectedMedia.type.startsWith('image/') && Math.floor(file.selectedMedia.size / oneKB) > fileSize)
    }

    setSubmitButtonDisable(isWebSizeExceeded || isMobileSizeExceeded)
  }

  const isImageButtonDisabled = submitButtonDisable || (selectedFileForMobile?.length > 0 ? selectedFileForMobile.some((file) => file?.isDurationExceeded || file?.isResolutionExceeded) : false)

  const handleDefaultCustomRender = () => {
    setRenderCustomEdit((prev) => !prev)
  }

  const handleModalClose = () => {
    closeModal()
    setUploadedFilesMobile([...submittedMobileImages])
    setUploadedFilesWeb([...submittedWebImages])
    setSelectedFileForWeb([...submittedWebImages])
    setSelectedFileForMobile([...submittedMobileImages])
    setDeletedNewsId([])
    setSelectedDefaultImages(newsFeedMedia.filter((el) => el?.defaultImageId || el?.idImage))
    setPreSelectedImage(newsFeedMedia.filter((el) => el.defaultImageId === 0))
  }
  const isDefaultImageSelected = (nameOfMedia) => selectedDefaultImages.some((image) => image.nameOfMedia === nameOfMedia)

  const MessageTemplate = ({ imageWidth, imageHeight, videoLimit, webImageLimit, mobileImageLimit }) => (
    <div className="media-constraints">
      <ul>
        <li>
          Image Resolution: {imageWidth}px × {imageHeight}px
        </li>
        <li>Image File Size: Upto {webImageLimit ? `${webImageLimit}MB` : `${mobileImageLimit}KB`}</li>
        <li>Video Duration: {videoLimit ? `Upto ${videoLimit} Mins` : 'No Restriction'}</li>
      </ul>
    </div>
  )

  return (
    <>
      <div className="thumbnail-image-heading">
        <div className="image-heading">{t('WEB_IMAGES')}</div>
        <div className="image-heading">{t('MOBILE_IMAGES')}</div>
      </div>
      <div className="image-display">
        <div className="web-images-display">
          {newsFeedMedia.map((ele, index) => (
            <>
              {(ele?.webimgpath || ele?.pathofmedia) && (
                <div key={index} className="added-image">
                  <div className="uploaded-image-close-button">
                    <Button icon="pi pi-times" className="p-button-outlined p-button-secondary close-button" onClick={() => removeImage(index, ele.nameOfMedia, ele.idNewsFeedMedia, ele?.webimgpath, 'web')} data-testid="close-button" />
                  </div>
                  <div className="image-size">{(ele.typeMedia === 'image' || ele.typeMedia === 'video') && ele.typeMedia === 'video' ? <video src={ele?.pathOfMedia || ele?.webimgpath} type={'video/mp4'} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" /> : <img src={ele?.pathOfMedia || ele?.webimgpath} alt="thumbnail" />}</div>
                </div>
              )}
            </>
          ))}
        </div>
        <div className="mobile-images-display">
          {newsFeedMedia.map((ele, index) => (
            <>
              {ele?.mobimgpath && (
                <div key={index} className="added-image">
                  <div className="uploaded-image-close-button">
                    <Button icon="pi pi-times" className="p-button-outlined p-button-secondary close-button" onClick={() => removeImage(index, ele.nameOfMedia, ele.idNewsFeedMedia, ele?.mobimgpath, 'mobile')} data-testid="close-button" />
                  </div>
                  {ele?.mobimgpath && <div className="image-size">{(ele.typeMedia === 'image' || ele.typeMedia === 'video') && ele.typeMedia === 'video' ? <video src={ele?.mobimgpath} type={'video/mp4'} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" /> : <img src={ele?.mobimgpath} alt="thumbnail" />}</div>}
                </div>
              )}
            </>
          ))}
        </div>
      </div>
      <Dialog visible={isModalOpen} onHide={() => handleModalClose()} header={<div className="modal-main-header">{t('EDIT_THUMBNAIL_IMG')}</div>} className="dialog-image-component" data-testid="edit-news-dialog">
        <div className="thumbnail-modal">
          {renderCustomEdit && (
            <>
              <div className="default-image-handler" onClick={() => handleDefaultCustomRender()}>
                {t('EDIT_DEFAULT_IMAGES')}
              </div>
            </>
          )}
          {!renderCustomEdit && (
            <>
              <div className="section-header">
                <span className="section-header-text">{t('SELECT_FROM_BELOW_IMAGES')}</span>
              </div>
              {defaultImages?.length > 0 && (
                <div className="card flex justify-content-center" data-testid="image-gallery">
                  <Galleria key={galleria} value={defaultImages} activeIndex={activeIndex} onItemChange={(e) => setActiveIndex(e.index)} showThumbnails={false} thumbnail={thumbnailTemplate} />
                  <div className="grid">
                    {defaultImages.map((image, index) => (
                      <div key={index} className={`default-img ${isDefaultImageSelected(image.nameOfMedia) ? 'selected-image' : 'default-image'}`} onClick={() => handleImageClick(index, image.idImage, image?.nameOfMedia)}>
                        <img src={image.imageUrl} alt="default" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="or-text">{t('OR')}</div>
          {!renderCustomEdit && (
            <div className="custom-image-handler" onClick={() => handleDefaultCustomRender()}>
              {t('EDIT_CUSTOM_IMAGES')}
            </div>
          )}

          {renderCustomEdit && (
            <div className="file-upload-wrapper">
              <div className="second-half-container">
                <div className="file-upload-header">
                  <span className="file-upload-text">{t('UPLOAD_FOR_WEB')}</span>
                  <MessageTemplate imageWidth={mobileImageResolutionWidth} imageHeight={mobileImageResolutionHeight} webImageLimit={webImageSizeLimit} />
                </div>

                <FileUpload
                  className="choose-file-button"
                  chooseLabel="Choose Files"
                  accept=".jpg,.jpeg,.png,.webp,.mp4"
                  auto={true}
                  data-testid="web-file-upload-button"
                  multiple={true}
                  onSelect={(event) => handleFileSelect(event, 'web')}
                  emptyTemplate={
                    <div>
                      {preSelectedImage?.length > 0 && (
                        <div className="default-images-zero-id-container">
                          {preSelectedImage?.map((item, index) => (
                            <>
                              {(item?.webimgpath || item?.pathOfMedia) && (
                                <div key={index} className="default-img">
                                  <div className="image-file">
                                    <div className=" add-image-container" data-testid="web-added-image-container">
                                      <div className="add-image-container">{item.typeMedia.startsWith('image') ? <img src={item?.pathOfMedia || item?.webimgpath} alt={`uploaded-file-${index}`} /> : <video src={item?.pathOfMedia || item?.webimgpath} type={item.typeMedia} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" />}</div>
                                    </div>
                                    <div className="cancel-action">
                                      <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => handleCancel(item, index)} data-testid="web-default-cancel-button" />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          ))}
                        </div>
                      )}
                      {uploadedFilesWeb.length > 0 &&
                        uploadedFilesWeb.map((file, index) => (
                          <div key={index} className="add-file-upload">
                            <div className="image-file">
                              <div className="add-image-container" data-testid="web-add-image-container">
                                <div className="add-image-container">{file.type.startsWith('image/') ? <img src={URL.createObjectURL(file)} alt={`uploaded-file-${index}`} /> : <video src={URL.createObjectURL(file)} type={file.type} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" />}</div>
                              </div>
                              <div className="cancel-action" data-testid="web-cancel-button">
                                <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => handleCancelClick(index, 'web', file.name)} />
                              </div>
                            </div>
                            {file?.type.startsWith('image/') && Math.floor(file.size / (oneKB * oneKB)) > webImageSizeLimit && <div className="size-error-message">{t('WEB_IMAGE_SIZE_LIMIT', { webImageSizeLimit })}</div>}
                          </div>
                        ))}
                      {uploadedFilesWeb.length === 0 && newsFeedMedia.length === 0 && (
                        <div className="drag-drop">
                          <p className="choose-title">{t('DRAG_AND_DROP_IMAGES')}</p>
                        </div>
                      )}
                    </div>
                  }
                />
              </div>
              <div className="second-half-container">
                <div className="file-upload-header">
                  <span className="file-upload-text">{t('UPLOAD_FOR_MOBILE')}</span>
                  <MessageTemplate imageWidth={mobileImageResolutionWidth} imageHeight={mobileImageResolutionHeight} mobileImageLimit={mobileImageSizeLimit} videoLimit={videoMaxDurationMins} />
                </div>
                <FileUpload
                  className="choose-file-button"
                  chooseLabel="Choose Files"
                  accept=".jpg,.jpeg,.png,.webp,.mp4"
                  data-testid="mobile-file-upload-button"
                  auto={true}
                  multiple={true}
                  onSelect={(event) => handleFileSelect(event, 'mobile')}
                  emptyTemplate={
                    <div>
                      {preSelectedImage?.length > 0 && (
                        <div className="default-images-zero-id-container">
                          {preSelectedImage?.map((item, index) => (
                            <>
                              {item?.mobimgpath && (
                                <div key={index} className="default-img">
                                  <div className="image-file">
                                    <div className=" add-image-container" data-testid="mobile-added-image-container">
                                      <div className="add-image-container">{item.typeMedia.startsWith('image') ? <img src={item.mobimgpath} alt={`uploaded-file-${index}`} /> : <video src={item.mobimgpath} type={item.typeMedia} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" />}</div>
                                    </div>
                                    <div className="cancel-action">
                                      <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => handleCancel(item, index)} data-testid="mobile-default-cancel-button" />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          ))}
                        </div>
                      )}
                      {uploadedFilesMobile.length > 0 &&
                        uploadedFilesMobile.map((file, index) => (
                          <div key={index} className="add-file-upload">
                            <div className="image-file">
                              <div className="add-image-container" data-testid="mobile-add-image-container">
                                <div className="add-image-container">{file.selectedMedia.type.startsWith('image/') ? <img src={URL.createObjectURL(file?.selectedMedia)} alt={`uploaded-file-${index}`} /> : <video src={URL.createObjectURL(file?.selectedMedia)} type={file.selectedMedia.type} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" />}</div>
                              </div>
                              <div className="cancel-action" data-testid="mobile-cancel-button">
                                <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => handleCancelClick(index, 'mobile')} />
                              </div>
                            </div>
                            {file?.isResolutionExceeded && <div className="size-error-message">{t('MOBILE_IMAGE_RESOLUTION_LIMIT', { mobileImageResolutionWidth, mobileImageResolutionHeight })}</div>}
                            {file?.isDurationExceeded && <div className="size-error-message">{t('VIDEO_DURATION_LIMIT', { videoMaxDurationMins })}</div>}
                            {file?.selectedMedia.type.startsWith('image/') && Math.floor(file.selectedMedia.size / oneKB) > mobileImageSizeLimit && <div className="size-error-message">{t('IMAGE_SIZE_LIMIT', { mobileImageSizeLimit })}</div>}
                          </div>
                        ))}
                      {uploadedFilesMobile.length === 0 && newsFeedMedia.length === 0 && (
                        <div className="drag-drop">
                          <p className="choose-title">{t('DRAG_AND_DROP_IMAGES')}</p>
                        </div>
                      )}
                    </div>
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div className="default-button-container">
          <ButtonComponent type="submit" onClick={handleImageSubmit} disabled={isImageButtonDisabled} />
        </div>

        <DialogModal {...modalState} onDismiss={onDismissFunction} onHide={onDismissFunction} />
      </Dialog>
      <Toast ref={toast} position="top-right" />
    </>
  )
}

export default Loader(EditModalComponent)
